import React from 'react'
import { Badge, Button, Col } from 'reactstrap'
import { ButtonContainer, StyledRow } from './component.styles'
import EditSiteModal from 'components/EditSiteModal'
import SiteSummary from '../SiteSummary'
import SiteUsers from 'components/SiteUsers'
import type { HistoricalReport } from 'store/reports/reducer'
import type { NetSuiteAsset, Site, SiteUser } from 'types'

interface ReportData {
  data?: any[]
  filename?: string
  headers?: string[]
}

interface ReportsState {
  [key: string]: string | ReportData | null
}

interface SiteProps {
  accessCodes?: string[]
  addBulkUsersResponse?: any
  backToSearch: () => void
  createSiteUser: (
    siteUser: Partial<SiteUser>,
    sendNotification: boolean,
    selectedSite?: Site,
  ) => Promise<void>
  createSiteUsers: (
    siteId: number,
    users: Partial<SiteUser>[],
    sendNotification: boolean,
  ) => Promise<void>
  error?: Error
  fetchLessonProgressReport: (siteId: number, skuCode: string) => void
  fetchTrainingReport: (
    siteUserEmails: string[],
    skuCode: string,
    siteName: string,
  ) => void
  handleCancelUserAccess: (siteUser: SiteUser) => void
  handleClearSiteCache: () => void
  handleGrantNewRole: (siteUser: SiteUser, newRole: string) => void
  historicalReports?: HistoricalReport[]
  hsHistoricalReports?: HistoricalReport[]
  isBulkOperationCompleted?: boolean
  isFetchingHistoricalReports?: boolean
  isFetchingHsHistoricalReports?: boolean
  isFetchingReports?: Record<string, boolean>
  remindSiteUser: (siteUser: SiteUser) => void
  remindSiteUsers?: (siteId: number, emailFilters: any) => void
  reports?: ReportsState
  resetBulkAddUsers: () => void
  showEditModal?: boolean
  site?: Site
  siteUsers?: SiteUser[] | null
  toggleEditModal: () => void
  siteNetSuiteAssets?: NetSuiteAsset[]
}

const Site: React.FC<SiteProps> = ({
  accessCodes,
  addBulkUsersResponse,
  backToSearch,
  createSiteUser,
  createSiteUsers,
  error,
  fetchLessonProgressReport,
  fetchTrainingReport,
  handleCancelUserAccess,
  handleClearSiteCache,
  handleGrantNewRole,
  historicalReports,
  hsHistoricalReports,
  isBulkOperationCompleted,
  isFetchingHistoricalReports,
  isFetchingHsHistoricalReports,
  isFetchingReports,
  remindSiteUser,
  remindSiteUsers,
  reports,
  resetBulkAddUsers,
  showEditModal,
  site,
  siteNetSuiteAssets,
  siteUsers,
  toggleEditModal,
}) =>
  site && (
    <>
      <p>
        <Button color="primary" onClick={backToSearch} outline>
          <i className="fas fa-chevron-left" />
          &nbsp; Back to Search
        </Button>
      </p>
      <div>
        <StyledRow>
          <Col>
            <h2 className="mt-2 text-light">
              Site&nbsp;
              <Badge color="light">SS: {site.id}</Badge>&nbsp;
            </h2>
          </Col>
          <Col>
            <ButtonContainer>
              <Button color="light" onClick={toggleEditModal}>
                <i className="fas fa-pencil-alt" />
                &nbsp;Edit
              </Button>
            </ButtonContainer>
            {site.inactive && (
              <h2 className="mt-2 text-light">
                <Badge className="badge-warning">Inactive</Badge>
              </h2>
            )}
          </Col>
        </StyledRow>
        <SiteSummary
          accessCodes={accessCodes}
          handleClearSiteCache={handleClearSiteCache}
          historicalReports={historicalReports}
          hsHistoricalReports={hsHistoricalReports}
          isFetchingHistoricalReports={isFetchingHistoricalReports}
          isFetchingHsHistoricalReports={isFetchingHsHistoricalReports}
          isFetchingReports={isFetchingReports}
          onFetchLessonProgressReport={fetchLessonProgressReport}
          onFetchTrainingReport={fetchTrainingReport}
          reports={reports}
          site={site}
          siteUsers={siteUsers || []}
          siteNetSuiteAssets={siteNetSuiteAssets}
        />
        {siteUsers && (
          <SiteUsers
            addBulkUsersResponse={addBulkUsersResponse}
            createSiteUser={createSiteUser}
            createSiteUsers={createSiteUsers}
            error={error}
            handleCancelUserAccess={handleCancelUserAccess}
            handleGrantNewRole={handleGrantNewRole}
            isBulkOperationCompleted={isBulkOperationCompleted}
            remindSiteUser={remindSiteUser}
            remindSiteUsers={remindSiteUsers}
            resetBulkAddUsers={resetBulkAddUsers}
            selectedSite={site}
            siteUsers={siteUsers}
          />
        )}
      </div>
      <EditSiteModal
        isOpen={showEditModal ?? false}
        site={site}
        toggle={toggleEditModal}
      />
    </>
  )

export default Site
